import React from "react";
import {
  ArrowRight,
  BadgeCheck,
  CircleDollarSign,
  FileSearch,
  Handshake,
  LifeBuoy,
  ListChecks,
  MapPin,
  PhoneCall,
  ReceiptText,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { Link } from "wouter";
import { CourtDecisionsFeature } from "./CourtDecisionsFeature";
import { courtFee } from "@/lib/site-data";

type LocalCtaProps = {
  variant?: "home" | "city";
  ctaHref?: string;
  ctaLabel?: string;
};

type InstallmentPaymentHighlightProps = {
  className?: string;
};

export function InstallmentPaymentHighlight({
  className = "",
}: InstallmentPaymentHighlightProps) {
  return (
    <div
      className={`installment-payment-highlight ${className}`.trim()}
      aria-label="Możliwość płatności w ratach"
    >
      <span className="installment-payment-symbol" aria-hidden="true">
        zł
      </span>
      <div>
        <strong>Płatność w ratach</strong>
        <span>
          Możliwa płatność w ratach - warunki omawiamy przed Twoją decyzją.
        </span>
      </div>
    </div>
  );
}

export function MeetingBenefits() {
  const benefits = [
    {
      icon: UsersRound,
      title: "Widzisz, z kim rozmawiasz",
      text: "Spotkanie zmienia anonimowy problem w rozmowę z konkretną osobą po drugiej stronie stołu.",
    },
    {
      icon: FileSearch,
      title: "Pokazujesz jedno pismo",
      text: "Nie musisz opowiadać wszystkiego od początku. Wystarczy dokument, który budzi dziś największy niepokój.",
    },
    {
      icon: Handshake,
      title: "Wychodzisz z planem",
      text: "Po rozmowie wiesz, co warto przygotować, co wymaga analizy i czy jest sens iść dalej.",
    },
    {
      icon: ShieldCheck,
      title: "Decydujesz bez presji",
      text: "Samo spotkanie nie oznacza zlecenia sprawy. Najpierw pytania, odpowiedzi i decyzja po Twojej stronie.",
    },
  ];
  return (
    <section className="meeting-benefits section-pad">
      <div className="shell-wide">
        <div className="asymmetric-heading">
          <div>
            <span className="section-kicker">rozmowa w kancelarii</span>
            <h2>
              W stresującej sprawie łatwiej rozmawiać przy stole niż przez
              formularz.
            </h2>
          </div>
          <p>
            Telefon może być pierwszym krokiem, ale wizyta na miejscu pozwala
            spokojnie zadać pytania, zobaczyć ludzi i wyjść z konkretnym planem.
          </p>
        </div>
        <div className="meeting-benefit-grid">
          {benefits.map(({ icon: Icon, title, text }, index) => (
            <article key={title}>
              <span>0{index + 1}</span>
              <Icon size={27} />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
        <div className="meeting-benefit-action">
          <div>
            <MapPin size={24} />
            <p>
              <strong>Wybierz najbliższą kancelarię.</strong> Termin
              potwierdzimy telefonicznie, a spotkanie jest bezpłatne.
            </p>
          </div>
          <Link className="button-primary" href="/kancelarie#umow-spotkanie">
            Bezpłatna konsultacja <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function PriceClarity({
  variant = "home",
  ctaHref = "#kontakt",
  ctaLabel = "Bezpłatna konsultacja",
}: LocalCtaProps) {
  const cityVariant = variant === "city";
  return (
    <section id="cena" className="price-clarity section-pad">
      <div className="shell-wide">
        <div className="price-clarity-grid">
          <div>
            <span className="section-kicker light-kicker">
              cena i zakres współpracy
            </span>
            <h2>
              Najpierw sprawdzamy, jak możemy pomóc. Potem poznajesz cenę.
            </h2>
            <p id="p02-size-exception" className="price-main-number">
              2 900 - 4 900 zł
              <span>za prowadzenie sprawy</span>
            </p>
            <p>
              <strong>I etap: od analizy do ogłoszenia upadłości.</strong> Cena
              obejmuje analizę Twojej sytuacji, skompletowanie dokumentów,
              przygotowanie i złożenie wniosku oraz prowadzenie sprawy aż do
              postanowienia sądu o ogłoszeniu upadłości.
            </p>
            <p className="court-fee-note">
              <strong>{courtFee.label}.</strong> {courtFee.explanation}
            </p>
            <p>
              <strong>Po ogłoszeniu upadłości</strong> możemy wspierać Cię dalej
              (m.in. w kontaktach z syndykiem i przy planie spłaty) - zakres i
              koszt ustalamy indywidualnie.
            </p>
            <InstallmentPaymentHighlight className="price-installment-highlight" />
            <div className="price-context">
              <span>Ilustracja proporcji</span>
              <strong>
                Przy zadłużeniu 80 000 zł kwota 2 900 zł odpowiada 3,63% tej
                sumy.
              </strong>
              <p>
                To przykład relacji kosztu do łącznego zadłużenia - nie
                obietnica umorzenia ani indywidualnego wyniku postępowania.
              </p>
            </div>
            {cityVariant ? (
              <div className="price-local-actions">
                <a className="button-primary" href={ctaHref}>
                  {ctaLabel} <ArrowRight size={18} />
                </a>
                <Link href="/cennik" className="text-link">
                  Zobacz cennik <ArrowRight size={17} />
                </Link>
              </div>
            ) : (
              <Link href="/cennik" className="button-on-dark">
                Zobacz dokładny zakres ceny <ArrowRight size={18} />
              </Link>
            )}
          </div>
          <div className="price-questions">
            <article>
              <ReceiptText size={26} />
              <h3>Cena ustalona przed decyzją</h3>
              <p>Cenę i zakres obsługi znasz przed podpisaniem umowy.</p>
            </article>
            <article>
              <ListChecks size={26} />
              <h3>Nie sama kwota długu określa pracę</h3>
              <p>
                Wyższy dług z małą liczbą wierzycieli może wymagać mniej
                czynności niż niższe zadłużenie rozproszone między wiele umów,
                pism i wierzycieli.
              </p>
            </article>
            <article>
              <BadgeCheck size={26} />
              <h3>Co obejmuje cena</h3>
              <p>
                Cena obejmuje także pisma na kolejnych etapach, obsługę w
                systemie KRZ i kontakt z nami do końca postępowania.
              </p>
            </article>
          </div>
        </div>
        <div className="price-assurance">
          <a href="#gwarancja" className="guarantee-badge">
            <ShieldCheck size={17} /> Gwarancja pisemna w umowie - przeczytasz
            przed decyzją.
          </a>
          <p>
            Zapytaj o wycenę swojej sprawy:{" "}
            <a href="tel:+48513513613">513 513 613</a>
          </p>
        </div>
      </div>
    </section>
  );
}

export function ReferralProgram() {
  return (
    <section className="referral-section section-pad">
      <div className="shell-narrow referral-card">
        <span className="section-kicker">program poleceń</span>
        <h2>Znasz osobę, która też potrzebuje spokojnej rozmowy?</h2>
        <p>
          Marka komunikuje 500 zł prowizji za polecenie osoby potrzebującej
          pomocy. Dokładne zasady programu potwierdzimy indywidualnie, z
          poszanowaniem prywatności i sytuacji osoby polecanej.
        </p>
        <a
          href="mailto:biuro@upadlosci-ekspert.pl?subject=Program%20polece%C5%84"
          className="text-link"
        >
          Zapytaj o zasady programu <ArrowRight size={17} />
        </a>
      </div>
    </section>
  );
}

export function GuaranteeFeature({
  variant = "home",
  ctaHref = "#kontakt",
  ctaLabel = "Bezpłatna konsultacja - przeczytaj umowę na miejscu",
}: LocalCtaProps) {
  const cityVariant = variant === "city";
  return (
    <section
      id="gwarancja"
      className="guarantee-feature"
      aria-labelledby="guarantee-title"
    >
      <div className="shell-wide guarantee-feature-grid">
        <div className="guarantee-document-stage">
          <CourtDecisionsFeature variant="inline" />
        </div>
        <div className="guarantee-copy">
          <span className="section-kicker">
            pisemna gwarancja wynagrodzenia
          </span>
          <h2 id="guarantee-title">
            Pisemna Gwarancja to zapis, który przeczytasz przed decyzją
          </h2>
          <p className="guarantee-ready-line">
            Prostą, jednostronicową umowę podpiszesz dopiero wtedy, gdy będziesz
            gotowy.
          </p>
          <p>
            Jeśli przy prawidłowo złożonym i opłaconym wniosku upadłość nie
            zostanie ogłoszona, wynagrodzenie nie jest należne, a otrzymane
            wpłaty zwracamy w 14 dni.
          </p>
          <div className="guarantee-points">
            <p>
              <ShieldCheck size={18} />
              <span>
                <strong>Gwarancja pisemna w umowie</strong> - nie jest hasłem z
                reklamy.
              </span>
            </p>
            <p>
              <FileSearch size={18} />
              <span>
                <strong>Warunki omawiasz przed decyzją</strong> - w prostym
                języku i bez pośpiechu.
              </span>
            </p>
            <p>
              <Handshake size={18} />
              <span>
                <strong>Umowę zabierzesz do domu</strong> - możesz wrócić, gdy
                będziesz gotowy.
              </span>
            </p>
          </div>
          <div className="guarantee-contract-terms">
            <h3>Gwarancja wynagrodzenia</h3>
            <p>
              Warunki gwarancji opisujemy w umowie przed decyzją o współpracy.
            </p>
            <p>
              Jeżeli wobec Zleceniodawcy nie zostałaby ogłoszona upadłość,
              wynagrodzenie nie będzie należne Zleceniobiorcy. Zleceniobiorca
              gwarantuje wówczas zwrot otrzymanych wpłat Zleceniodawcy w
              terminie 14 dni.
            </p>
          </div>
          <div className="guarantee-actions">
            <a className="button-primary" href={ctaHref}>
              {ctaLabel} <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </div>
      {!cityVariant && (
        <div className="test-teaser shell-wide">
          <span>
            Sprawdź anonimowo w 2 minuty, czy możesz umorzyć swoje długi.
          </span>
          <Link href="/#test">
            Zrób test <ArrowRight size={17} />
          </Link>
        </div>
      )}
    </section>
  );
}

export function BrandBenefits() {
  const benefits = [
    {
      icon: BadgeCheck,
      title: "5 789 przeprowadzonych upadłości",
      text: "To liczba przekazana przez markę. Każda sprawa wymaga osobnej analizy.",
    },
    {
      icon: FileSearch,
      title: "Kompleksowa współpraca",
      text: "Możesz ustalić zakres pomocy. Zespół pomaga w dokumentach, wniosku i dalszych formalnościach.",
    },
    {
      icon: CircleDollarSign,
      title: "Decydujesz, jak zapłacisz",
      text: "Możliwa płatność w ratach pozwala dopasować sposób rozliczenia do sytuacji klienta.",
    },
  ];
  return (
    <section className="brand-benefits section-pad">
      <div className="shell-wide">
        <div className="section-lead">
          <span className="section-kicker">dlaczego właśnie my</span>
          <h2>
            Ważne są nie tylko liczby. Ważne jest, co dzieje się po pierwszym
            telefonie.
          </h2>
          <p>
            Trzy informacje z aktualnego przekazu marki, wyjaśnione spokojnie i
            bez obiecywania identycznego rezultatu w każdej sprawie.
          </p>
        </div>
        <div className="brand-benefit-grid">
          {benefits.map(({ icon: Icon, title, text }, index) => (
            <article key={title}>
              <span>0{index + 1}</span>
              <Icon size={29} />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AfterBankruptcySupport({
  ctaHref = "/#kontakt",
  ctaLabel = "Opowiedz, co jest dziś najpilniejsze",
}: LocalCtaProps) {
  return (
    <section
      className="after-bankruptcy-support section-pad"
      aria-labelledby="after-bankruptcy-title"
    >
      <div className="shell-wide after-bankruptcy-grid">
        <div>
          <span className="section-kicker">
            wsparcie także po ogłoszeniu upadłości
          </span>
          <h2 id="after-bankruptcy-title">
            Nie zostajesz sam po ogłoszeniu upadłości.
          </h2>
          <p>
            Upadłość została ogłoszona. Co dalej? Wiele firm kończy pomoc na
            etapie złożenia wniosku. My pomagamy również później.
          </p>
          <p>
            Pomagamy przy kontakcie z syndykiem, ankiecie, portalu Krajowego
            Rejestru Zadłużonych, pismach i planie spłaty.
          </p>
          <a className="button-primary" href={ctaHref}>
            {ctaLabel} <ArrowRight size={18} />
          </a>
        </div>
        <aside className="after-bankruptcy-card">
          <LifeBuoy size={34} />
          <h3>Pomoc przy dalszych formalnościach</h3>
          <ul>
            <li>Kontakt z syndykiem i wsparcie przy ankiecie.</li>
            <li>Obsługa pism oraz portalu Krajowego Rejestru Zadłużonych.</li>
            <li>Pomoc przy planie spłaty i dalszych formalnościach.</li>
          </ul>
          <small>
            Zakres dalszego wsparcia oceniamy po rozmowie i analizie dokumentów
            danej sprawy.
          </small>
        </aside>
      </div>
    </section>
  );
}

export function FearRelief({
  ctaHref = "/#kontakt",
  ctaLabel = "Wybierz najłatwiejszy sposób kontaktu",
}: LocalCtaProps) {
  return (
    <section
      className="fear-relief section-pad"
      aria-labelledby="fear-relief-title"
    >
      <div className="shell-narrow">
        <span className="section-kicker">pierwsza rozmowa bez oceniania</span>
        <h2 id="fear-relief-title">
          Wstyd i lęk są częścią tej sytuacji. Nie musisz przez nie przechodzić
          sam.
        </h2>
        <p className="fear-relief-lead">
          Długi, zajęcie wynagrodzenia albo niezrozumiałe pismo mogą powodować
          napięcie. Rozmowę zaczynamy od tego, co jest dla Ciebie najpilniejsze
          - bez oceny i bez konieczności opowiadania wszystkiego od razu.
        </p>
        <div className="fear-relief-grid">
          <article>
            <span>01</span>
            <h3>Nie musisz znać wszystkich kwot</h3>
            <p>
              Na początku wystarczy zarys sytuacji albo jedno pismo, które budzi
              największy niepokój.
            </p>
          </article>
          <article>
            <span>02</span>
            <h3>Nie musisz nic podpisywać</h3>
            <p>
              Rozmowa służy zrozumieniu możliwości. Decyzja o dalszych krokach
              pozostaje po Twojej stronie.
            </p>
          </article>
          <article>
            <span>03</span>
            <h3>Nie musisz się tłumaczyć</h3>
            <p>
              Trudna sytuacja finansowa nie definiuje człowieka. Rozmawiamy
              konkretnie i spokojnie.
            </p>
          </article>
        </div>
        <a className="text-link" href={ctaHref}>
          {ctaLabel} <ArrowRight size={17} />
        </a>
      </div>
    </section>
  );
}

export function OfficeMapFeature() {
  return (
    <section className="office-map-feature section-pad">
      <div className="shell-wide office-map-grid">
        <div>
          <span className="section-kicker">
            6 kancelarii i obsługa całego kraju
          </span>
          <h2>Pomoc blisko Ciebie - na miejscu albo zdalnie.</h2>
          <p>
            Wybierz kancelarię, do której chcesz przyjechać. Jeśli najbliższy
            punkt jest daleko, rozpoczniemy od rozmowy telefonicznej i ustalimy
            wygodny sposób kontaktu.
          </p>
          <Link href="/kancelarie" className="button-primary">
            Zobacz wszystkie kancelarie <ArrowRight size={18} />
          </Link>
        </div>
        <figure>
          <picture>
            <source
              type="image/avif"
              srcSet="/manus-storage/expert-partner-office-map-550_aebcb5fd.avif 550w, /manus-storage/expert-partner-office-map-800_d2108977.avif 800w, /manus-storage/expert-partner-office-map-1024_e1e4235d.avif 1024w"
              sizes="(max-width: 767px) 100vw, 50vw"
            />
            <source
              type="image/webp"
              srcSet="/manus-storage/expert-partner-office-map-550_5746cbe1.webp 550w, /manus-storage/expert-partner-office-map-800_a2b415c6.webp 800w, /manus-storage/expert-partner-office-map-1024_4748cf37.webp 1024w"
              sizes="(max-width: 767px) 100vw, 50vw"
            />
            <img
              loading="lazy"
              decoding="async"
              src="/manus-storage/expert-partner-office-map-1024_4748cf37.webp"
              width="1024"
              height="934"
              alt="Mapa Polski z lokalizacjami kancelarii Upadłości Ekspert"
            />
          </picture>
          <figcaption>
            Mapa pokazuje 6 kancelarii oraz obsługę w całej Polsce.
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
